import { CloseOutlined } from '@mui/icons-material';
import { Alert, Box, CircularProgress, IconButton, Stack } from '@mui/material';
import { createColumnHelper, RowSelectionState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';

import AllocationGroupAutocompleteForUser from '@/components/features/UserDetails/components/AllocationGroupAutocompleteForUser';
import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import IndeterminateCheckbox from '@/components/shared/Table/IndeterminateCheckbox';
import TableSelectionActions from '@/components/shared/Table/TableSelectionActions';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useAllocationGroupByUser } from '@/hooks/useAllocationGroupByUser';
import { EnhancedAllocationGroup } from '@/hooks/useManageUserAllocationGroups';
import { useManageUserAllocationGroups } from '@/hooks/useManageUserAllocationGroups';
import { useSnackbar } from '@/hooks/useSnackbar';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

interface AllocationGroupsTabProps {
  userId?: string;
  email?: string;
}

type AllocationGroupItem = NonNullable<
  components['schemas']['GetAllocationGroupByUserEndpoint_GetAllocationGroupByUserEndpointPagedResponse']['items']
>[number];

const columnHelper = createColumnHelper<AllocationGroupItem>();

function AllocationGroupsTab({ userId, email }: AllocationGroupsTabProps) {
  const { organizationId } = useOrganizationId();
  const snackbar = useSnackbar();
  const [selectedGroupNames, setSelectedGroupNames] = useState<EnhancedAllocationGroup[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const {
    addGroups,
    removeGroup,
    removeGroups,
    isMutating: isManagingGroups,
    removeGroupMutation,
    removeGroupsMutation,
  } = useManageUserAllocationGroups({
    snackbar,
  });

  const handleAddGroups = async () => {
    if (!email || selectedGroupNames.length === 0 || isManagingGroups) {
      return;
    }

    try {
      await addGroups(email, selectedGroupNames);

      setSelectedGroupNames([]);
      setRowSelection({});
      snackbar.showSuccess('Allocation groups added successfully');
      // Trigger refetch by invalidating queries (handled by the hook)
    } catch (err) {
      handleSnackbarError(err, snackbar, 'Failed to add allocation groups');
    }
  };

  // Get pagination state from URL (single source of truth)
  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { allocationGroups, totalCount, isLoading, isRefetching, error } = useAllocationGroupByUser(
    userId || '',
    apiPage,
    pagination.pageSize,
  );

  const allocationGroupsList = useMemo(() => allocationGroups?.items ?? [], [allocationGroups?.items]);

  const ensureValidPageAfterRemove = useCallback(
    (newTotalCount: number) => {
      const maxPageIndex = Math.max(0, Math.ceil(newTotalCount / pagination.pageSize) - 1);

      if (pagination.pageIndex > maxPageIndex) {
        setPagination((prev) => ({
          ...prev,
          pageIndex: maxPageIndex,
        }));
      }
    },
    [pagination.pageIndex, pagination.pageSize, setPagination],
  );

  const handleRemoveGroup = useCallback(
    async (allocationGroupId: number, currentUserId: string) => {
      if (!allocationGroupId || !currentUserId) {
        return;
      }

      try {
        const success = await removeGroup(allocationGroupId, currentUserId);

        if (success) {
          setRowSelection({});
          ensureValidPageAfterRemove(Math.max(0, totalCount - 1));
          snackbar.showSuccess('Allocation group removed successfully');
          return;
        }

        snackbar.showError('Failed to remove allocation group');
      } catch (err) {
        handleSnackbarError(err, snackbar, 'Failed to remove allocation group');
      }
    },
    [ensureValidPageAfterRemove, removeGroup, snackbar, totalCount],
  );

  const handleRemoveSelectedGroups = useCallback(async () => {
    if (!email || isManagingGroups) {
      return;
    }

    const groupIdsToRemove = allocationGroupsList
      .map((allocationGroup) => allocationGroup.group?.id)
      .filter((groupId): groupId is number => !!groupId && rowSelection[groupId.toString()])
      .filter((groupId) => groupId > 0);

    if (groupIdsToRemove.length === 0) {
      return;
    }

    try {
      const success = await removeGroups(email, groupIdsToRemove);

      if (success) {
        const removedCount = groupIdsToRemove.length;
        ensureValidPageAfterRemove(Math.max(0, totalCount - removedCount));
        setRowSelection({});
        snackbar.showSuccess('Selected groups removed successfully');
        return;
      }

      snackbar.showError('Failed to remove selected allocation groups');
    } catch (err) {
      handleSnackbarError(err, snackbar, 'Failed to remove selected allocation groups');
    }
  }, [allocationGroupsList, email, ensureValidPageAfterRemove, isManagingGroups, removeGroups, rowSelection, snackbar, totalCount]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <IndeterminateCheckbox
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          </Box>
        ),
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <IndeterminateCheckbox
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          </Box>
        ),
        size: 5,
        minSize: 5,
        maxSize: 5,
      }),
      columnHelper.accessor('group.name', {
        header: 'Inventory Allocations',
        size: 70,
        cell: (info) => <span data-testid={`group_name-${info.row.original.group?.id}`}>{info.row.original.group?.name || '-'}</span>,
      }),
      columnHelper.display({
        id: 'actions',
        size: 30,
        cell: (info) => {
          const rowGroup = info.row.original.group;
          const rowGroupId = rowGroup?.id;

          if (!rowGroupId) {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <IconButton size='small' disabled data-testid='remove-allocation-groups-unknown' sx={{ padding: '4px' }}>
                  <CloseOutlined sx={{ fontSize: '14px' }} />
                </IconButton>
              </Box>
            );
          }

          const isRemovingCurrentGroup =
            removeGroupMutation.variables?.groupId === rowGroupId && (isRefetching || removeGroupMutation.isPending);

          const isRemovingInBatch =
            removeGroupsMutation.variables?.groupIds.includes(rowGroupId) && (isRefetching || removeGroupsMutation.isPending);

          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (email && rowGroupId) {
                    handleRemoveGroup(rowGroupId, email);
                  }
                }}
                disabled={
                  isManagingGroups ||
                  isRemovingCurrentGroup ||
                  isRemovingInBatch ||
                  isRefetching ||
                  removeGroupMutation.isPending ||
                  removeGroupsMutation.isPending
                }
                data-testid={`remove-allocation-groups-${rowGroupId}`}
                sx={{ padding: '4px' }}
              >
                {isRemovingCurrentGroup || isRemovingInBatch ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <CloseOutlined sx={{ fontSize: '14px' }} />
                )}
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    [
      email,
      handleRemoveGroup,
      isManagingGroups,
      isRefetching,
      removeGroupMutation.variables?.groupId,
      removeGroupsMutation.variables?.groupIds,
      removeGroupMutation.isPending,
      removeGroupsMutation.isPending,
    ],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setRowSelection({});
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newPageSize = parseInt(event.target.value, 10);

    setRowSelection({});
    setPagination({ pageIndex: 0, pageSize: newPageSize });
  };

  const tableWithData = useBaseTable<AllocationGroupItem>({
    tableData: allocationGroupsList,
    columns,
    isRowSelectionEnabled: true,
    rowSelection,
    onRowSelectionChange: setRowSelection,
    isColumnOrderEnabled: false,
    isColumnVisibilityEnabled: false,
    hasPagination: true,
    pagination,
    onPaginationChange: setPagination,
    paginationCount: totalCount,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
    getRowId: (row, index) => row.group?.id?.toString() ?? `row-${index}`,
  });

  const selectedCount = Object.keys(rowSelection).length;

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-allocations'
      >
        Error loading allocations: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Stack
        direction='row'
        spacing={1}
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap-reverse',
        }}
      >
        <TableSelectionActions
          selectedCount={selectedCount}
          totalCount={allocationGroupsList.length}
          onRemove={handleRemoveSelectedGroups}
          selectionLabel='Selected Groups'
          removeButtonText='Remove Groups'
          testIdPrefix='selected-allocation-groups'
        />

        <AllocationGroupAutocompleteForUser
          userId={userId || ''}
          organizationId={organizationId}
          value={selectedGroupNames}
          onChange={(_, newValue) => setSelectedGroupNames(newValue)}
          onApply={handleAddGroups}
          disabled={isManagingGroups || isRefetching}
        />
      </Stack>

      <BaseTable<AllocationGroupItem>
        {...tableWithData}
        testId='inventory-allocation-groups-table'
        ariaLabel='Inventory Allocation Groups list'
        noDataMessage='No inventory allocation groups assigned.'
        loadingData={{ isLoading: isLoading, loadingMessage: 'Loading...' }}
      />
    </Box>
  );
}

export default AllocationGroupsTab;
